import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface VendorBranding {
  vendorId: number | null;
  vendorName: string | null;
  logo: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;
}

interface VendorBrandingContextType {
  branding: VendorBranding;
  setBranding: (branding: Partial<VendorBranding>) => void;
  clearBranding: () => void;
}

const defaultBranding: VendorBranding = {
  vendorId: null,
  vendorName: null,
  logo: null,
  primaryColor: null,
  secondaryColor: null,
  backgroundColor: null,
  textColor: null,
};

const VendorBrandingContext = createContext<VendorBrandingContextType | undefined>(undefined);

export const VendorBrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branding, setBrandingState] = useState<VendorBranding>(() => {
    // Try to restore from sessionStorage on mount
    try {
      const stored = sessionStorage.getItem('vendorBranding');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to parse stored vendor branding:', e);
    }
    return defaultBranding;
  });

  const setBranding = useCallback((newBranding: Partial<VendorBranding>) => {
    setBrandingState(prev => {
      const updated = { ...prev, ...newBranding };
      // Persist to sessionStorage
      try {
        sessionStorage.setItem('vendorBranding', JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to store vendor branding:', e);
      }
      return updated;
    });
  }, []);

  const clearBranding = useCallback(() => {
    setBrandingState(defaultBranding);
    try {
      sessionStorage.removeItem('vendorBranding');
    } catch (e) {
      console.error('Failed to clear vendor branding:', e);
    }
  }, []);

  return (
    <VendorBrandingContext.Provider value={{ branding, setBranding, clearBranding }}>
      {children}
    </VendorBrandingContext.Provider>
  );
};

export const useVendorBranding = (): VendorBrandingContextType => {
  const context = useContext(VendorBrandingContext);
  if (!context) {
    throw new Error('useVendorBranding must be used within a VendorBrandingProvider');
  }
  return context;
};

export default VendorBrandingContext;
